import {DateTime} from 'luxon';

import {useMemo} from 'react';

import {SendIcon} from '@time-webkit/all/icons/send';
import {ActionButton} from '@time-webkit/all/molecules/icon-button';
import {UserListItem} from '@time-webkit/all/molecules/user-list-item';
import {Avatar} from '@time-webkit/all/atoms/avatar';

import type {Reaction} from '@mattermost/types/reactions';
import {getEmojiUrlByName} from 'utils/emoji_utils';
import {Client4} from 'mattermost-redux/client';
import type {User} from 'features/users';

import {useAppSelector} from 'stores/redux_store';

import {getUserDisplayName} from 'features/users/selectors/get_user_display_name';

import {getUser} from 'mattermost-redux/selectors/entities/users';

import {getFullName} from 'mattermost-redux/utils/user_utils';

import {EmojiWithTooltip} from './emoji_with_tooltip';
import styles from './styles.module.css';

interface ReactionListItemProps {
    userId: User['id'];
    reaction: Reaction;
    isMobile: boolean;
    isMilitaryTime: boolean;
    jumpToDirect: (username: string) => void;
}

export const ReactionListItem: React.FC<ReactionListItemProps> = ({
    userId,
    reaction,
    jumpToDirect,
    isMobile,
    isMilitaryTime,
}) => {
    const user = useAppSelector((state) => getUser(state, userId));
    const avatar = useMemo(() => {
        if (user?.id) {
            return Client4.getProfilePictureUrl(user?.id, user?.last_picture_update || Date.now());
        }

        return '';
    }, [user]);

    const hasActionButtons = !isMobile && user?.username;

    const emojiUrl = useAppSelector((state) => getEmojiUrlByName(state, reaction.emoji_name));
    const date = DateTime.fromMillis(reaction.create_at).toLocaleString({
        ...DateTime.DATETIME_FULL_WITH_SECONDS,
        year: undefined,
        second: undefined,
        timeZoneName: undefined,
        hour12: !isMilitaryTime,
    });

    const displayName = useAppSelector((state) => getUserDisplayName(state, user, true));
    const fullName = getFullName(user);

    const handleListItemClick = () => {
        if (user?.username) {
            jumpToDirect(user.username);
        }
    };

    const GoToDirectButton = ({username}: {username: string}) => (
        <ActionButton
            onPress={() => jumpToDirect(username)}
            className={styles.btn}
        >
            <SendIcon
                width={16}
                height={16}
                fillOpacity={0.56}
            />
        </ActionButton>
    );

    return (
        <UserListItem
            textPrimary={
                <div className={styles.name__container}>
                    <span>{displayName}</span>
                    {user?.username && <span className={styles.username}>{` ∙ @${user.username}`}</span>}
                </div>
            }
            onClick={isMobile ? handleListItemClick : undefined}
            icon={
                <EmojiWithTooltip
                    emojiName={reaction.emoji_name}
                    emojiUrl={emojiUrl}
                />
            }
            avatar={
                <Avatar
                    size={32}
                    username={fullName}
                    src={avatar}
                />
            }
            actionButtons={
                hasActionButtons ? [
                    <GoToDirectButton
                        key={reaction.emoji_name}
                        username={user?.username}
                    />,
                ] : []
            }
            className={styles.userlist__item}
            textSecondary={date}
        />
    );
};
